import { useMemo, useState } from "react";
import TheaterChrome from "./theaterChrome";
import CompareColumn from "./CompareColumn";
import CompareTableModal from "./CompareTableModal";
import CompareScorecardPanel from "./CompareScorecardPanel";
import { computeDiscrepancies, slotsWithDiscrepancies } from "./compareDiscrepancies";
import { computeCompareScorecard } from "./computeCompareScorecard";

function modelLabelForSlot(slot, models = []) {
    const meta = models.find((m) => m.slot === slot);
    if (meta?.label) return meta.label;
    if (meta?.id) {
        const s = String(meta.id).replace(/^openrouter:/, "");
        const parts = s.split("/");
        return parts[parts.length - 1] || s;
    }
    return `Slot ${slot}`;
}

function DiscrepancyStrip({ discrepancies = [], onOpenTable, onOpenTurn }) {
    const count = discrepancies.length;
    if (count === 0) {
        return (
            <div
                className="sticky top-[52px] z-10 mb-3 rounded border border-green-800/50 bg-green-950/40 px-3 py-2 text-xs text-green-200 flex items-center justify-between gap-2"
                data-testid="compare-discrepancy-strip"
            >
                <span>Sem discrepâncias detectadas</span>
                <button
                    type="button"
                    onClick={onOpenTable}
                    className="text-laranjinha hover:underline shrink-0"
                    data-testid="compare-ver-tabela"
                >
                    Ver tabela
                </button>
            </div>
        );
    }

    return (
        <div
            className="sticky top-[52px] z-10 mb-3 rounded border border-amber-700/50 bg-amber-950/40 px-3 py-2"
            data-testid="compare-discrepancy-strip"
        >
            <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-medium text-amber-100">
                    {count} discrepância{count !== 1 ? "s" : ""}
                </p>
                <button
                    type="button"
                    onClick={onOpenTable}
                    className="text-laranjinha hover:underline text-xs shrink-0"
                    data-testid="compare-ver-tabela"
                >
                    Ver tabela
                </button>
            </div>
            <ul className="text-xs text-amber-50/90 space-y-0.5 list-disc list-inside">
                {discrepancies.map((d, i) => (
                    <li key={`${d.turnId}-${d.type}-${i}`}>
                        <button
                            type="button"
                            className="text-left hover:underline"
                            onClick={() => onOpenTurn?.(d.turnId)}
                            data-testid={`compare-discrepancy-item-${i}`}
                        >
                            <span className="text-amber-200/70">{d.turnId}:</span> {d.summary}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function CompareTheater({
    run,
    title,
    onBack,
    isLive = false,
}) {
    const [tableOpen, setTableOpen] = useState(false);
    const [focusTurnId, setFocusTurnId] = useState(null);

    const models = useMemo(
        () => (Array.isArray(run?.models) ? run.models : []),
        [run?.models]
    );
    const results = useMemo(
        () => (Array.isArray(run?.results) ? run.results : []),
        [run?.results]
    );
    const progress = run?.progress;
    const progressSlots = Array.isArray(progress?.slots) ? progress.slots : [];

    const discrepancies = useMemo(() => {
        if (Array.isArray(run?.discrepancies) && run.discrepancies.length > 0) {
            return run.discrepancies;
        }
        return computeDiscrepancies(results, models);
    }, [run?.discrepancies, results, models]);

    const discrepancySlots = useMemo(
        () => slotsWithDiscrepancies(discrepancies),
        [discrepancies]
    );

    const slots = models.length > 0
        ? models.map((m) => m.slot).sort((a, b) => a - b)
        : [...new Set(results.map((r) => r.slot).filter((s) => s != null))].sort((a, b) => a - b);

    const openTable = (turnId = null) => {
        setFocusTurnId(turnId);
        setTableOpen(true);
    };

    const isTerminal =
        run?.status === "completed" || run?.status === "failed";

    const scorecard = useMemo(() => {
        if (!isTerminal) return null;
        if (run?.scorecard) return run.scorecard;
        return computeCompareScorecard({
            results,
            models,
            suiteSnapshot: run?.suite_snapshot,
        });
    }, [isTerminal, run?.scorecard, run?.suite_snapshot, results, models]);

    return (
        <div data-testid="compare-theater" data-mode={isLive ? "live" : "replay"}>
            <TheaterChrome
                title={title}
                status={run?.status}
                progress={isLive ? progress : null}
                onBack={onBack}
            />
            <DiscrepancyStrip
                discrepancies={discrepancies}
                onOpenTable={() => openTable(null)}
                onOpenTurn={(turnId) => openTable(turnId)}
            />
            {scorecard ? (
                <CompareScorecardPanel
                    scorecard={scorecard}
                    models={models}
                    onFocusTurn={(turnId) => openTable(turnId)}
                />
            ) : null}
            <div
                className={`grid gap-3 ${
                    slots.length >= 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
                }`}
                data-testid="compare-columns-grid"
            >
                {slots.map((slot) => {
                    const slotResults = results.filter((r) => r.slot === slot);
                    const slotProgress = progressSlots.find((s) => s.slot === slot);
                    return (
                        <CompareColumn
                            key={slot}
                            slot={slot}
                            modelLabel={modelLabelForSlot(slot, models)}
                            results={slotResults}
                            hasDiscrepancy={discrepancySlots.has(slot)}
                            slotProgress={slotProgress}
                            onVerMais={() => openTable(null)}
                        />
                    );
                })}
            </div>
            <CompareTableModal
                open={tableOpen}
                onClose={() => {
                    setTableOpen(false);
                    setFocusTurnId(null);
                }}
                results={results}
                models={models}
                progress={isLive ? progress : progress}
                suiteSnapshot={run?.suite_snapshot}
                focusTurnId={focusTurnId}
            />
        </div>
    );
}
